import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AppRoutes from './Routes/Routes';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Breadcrumbs from './components/Breadcrumbs/Breadcrumbs';
import Sidebar from './components/Sidebar/Sidebar';
import Poster from './components/Poster/Poster';
import HomeCategories from './components/HomePage/HomeCategories';
import HomeTopProducts from './components/HomePage/HomeTopProducts';
import Preloader from './components/Preloader/Preloader';
import { getCategories } from './Store/categories/categoriesSlice';
import { getProducts } from './Store/products/productsSlice';
import { getContacts } from './Store/contacts/contactsSlice';
import { getPublicReviews } from './Store/reviews/reviewsSlice';
import { initAuth } from './Store/user/userSlice';
import UserForm from './User/UserForm';
import { applyHomeSeo, applyNoIndexSeo } from './utils/seo';
import {
  buildOrganizationJsonLd,
  buildReviewsJsonLd,
  buildWebSiteJsonLd,
  removeJsonLd,
  upsertJsonLd,
} from './utils/jsonLd';
import { initAnalytics, trackPageView } from './utils/analytics';
import { useCatalogSyncHeight } from './hooks/useCatalogSyncHeight';
import InstallAppBanner from './components/common/InstallAppBanner';
import CallbackModal from './components/Callback/CallbackModal';
import {
  hideBootSplash,
  isStandalonePwa,
  removeBootSplashImmediately,
} from './utils/bootSplash';

const PRIVATE_ROUTES = ['/cart', '/checkout', '/favorites', '/orders', '/search', '/profile'];
const INFO_ROUTES = ['/delivery', '/payment'];
const SPLASH_FADE_MS = 450;
const useVideoSplash = isStandalonePwa();

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { sessionInitialized } = useSelector((state) => state.user);
  const categoriesLoading = useSelector((state) => state.categories.isLoading);
  const productsLoading = useSelector((state) => state.products.isLoading);
  const contactsLoading = useSelector((state) => state.contacts.isLoading);
  const contacts = useSelector((state) => state.contacts.data);
  const homeReviews = useSelector((state) => state.reviews.homeReviews);
  const [showBootPreloader, setShowBootPreloader] = useState(true);
  const [bootPreloaderExiting, setBootPreloaderExiting] = useState(false);
  const [splashVideoEnded, setSplashVideoEnded] = useState(!useVideoSplash);
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const isBooting = !sessionInitialized || categoriesLoading || productsLoading || contactsLoading;
  const isAdminPage = location.pathname === '/admin';
  const isHomePage = location.pathname === '/';
  const isCategoryPage = /^\/categories\/[^/]+/.test(location.pathname);
  const isProductPage = /^\/products\/[^/]+/.test(location.pathname);
  const showPoster = isHomePage || isCategoryPage;
  const showHeroTopGroup = showPoster || isHomePage;
  const catalogMaxHeight = useCatalogSyncHeight({
    enabled: showPoster || isProductPage,
    isHomePage,
    isCategoryPage,
    isProductPage,
    pathname: location.pathname,
  });

  const openCallback = useCallback(() => setIsCallbackOpen(true), []);
  const closeCallback = useCallback(() => setIsCallbackOpen(false), []);

  useEffect(() => {
    if (!useVideoSplash) {
      removeBootSplashImmediately();
    }
  }, []);

  useEffect(() => {
    dispatch(initAuth());
    initAnalytics();
  }, [dispatch]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getProducts());
    dispatch(getContacts());
    dispatch(getPublicReviews());
  }, [dispatch]);

  useEffect(() => {
    if (isBooting) return undefined;
    if (useVideoSplash && !splashVideoEnded) return undefined;

    setBootPreloaderExiting(true);
    if (useVideoSplash) {
      hideBootSplash();
    }
    const hideTimer = setTimeout(() => setShowBootPreloader(false), SPLASH_FADE_MS);

    return () => clearTimeout(hideTimer);
  }, [isBooting, splashVideoEnded]);

  const skipBootSplash = () => {
    setSplashVideoEnded(true);
    setBootPreloaderExiting(true);
    if (useVideoSplash) {
      hideBootSplash();
    }
    setTimeout(() => setShowBootPreloader(false), SPLASH_FADE_MS);
  };

  const handleSplashVideoEnded = useCallback(() => {
    setSplashVideoEnded(true);
  }, []);

  useEffect(() => {
    const path = location.pathname;

    if (path === '/admin' || PRIVATE_ROUTES.includes(path)) {
      applyNoIndexSeo();
      removeJsonLd('seo-website');
      removeJsonLd('seo-organization');
      removeJsonLd('seo-reviews');
      return;
    }

    if (path === '/') {
      applyHomeSeo();
      upsertJsonLd('seo-website', buildWebSiteJsonLd());
      upsertJsonLd('seo-organization', buildOrganizationJsonLd(contacts));
      upsertJsonLd('seo-reviews', buildReviewsJsonLd(homeReviews));
      return;
    }

    if (isCategoryPage || isProductPage || INFO_ROUTES.includes(path)) {
      removeJsonLd('seo-website');
      removeJsonLd('seo-organization');
      removeJsonLd('seo-reviews');
      return;
    }

    applyNoIndexSeo('404 — Сторінку не знайдено');
    removeJsonLd('seo-website');
    removeJsonLd('seo-organization');
    removeJsonLd('seo-reviews');
  }, [location.pathname, isCategoryPage, isProductPage, contacts, homeReviews]);

  const bootPreloader = showBootPreloader ? (
    <Preloader
      fullScreen
      exiting={bootPreloaderExiting}
      message="Запуск двигуна…"
      videoSplash={useVideoSplash}
      onVideoEnded={handleSplashVideoEnded}
      onSkip={useVideoSplash ? skipBootSplash : undefined}
    />
  ) : null;

  if (isAdminPage) {
    return (
      <>
        {bootPreloader}
        <AppRoutes />
      </>
    );
  }

  return (
    <>
      {bootPreloader}
    <div className="app">
      <Header onOpenCallback={openCallback} />
      <UserForm />
      <Breadcrumbs />
      <div
        className={`container${showPoster ? ' containerWithHero' : ''}${isHomePage ? ' containerHome' : ''}${isProductPage ? ' containerProduct' : ''}`}
      >
        <Sidebar
          layout={showPoster ? 'hero' : 'default'}
          catalogMaxHeight={catalogMaxHeight}
          onCallbackRequest={openCallback}
        />
        <div className="mainColumn">
          {showHeroTopGroup && (
            <div className="heroTopGroup">
              {showPoster && (
                <div className="heroSlot">
                  <Poster />
                </div>
              )}
              {isHomePage && (
                <div className="heroCategoriesSlot">
                  <HomeCategories />
                </div>
              )}
            </div>
          )}
          {isHomePage && (
            <div className="heroTopProductsSlot">
              <HomeTopProducts />
            </div>
          )}
          <main className="mainContent">
            <AppRoutes />
          </main>
        </div>
      </div>
      <Footer />
      <InstallAppBanner />
      <CallbackModal open={isCallbackOpen} onClose={closeCallback} />
    </div>
    </>
  );
}

export default App;
